import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import PropertyCard from "./property-card";
import PropertyMap from "./property-map";
import PropertyDetailModal from "./property-detail-modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Grid, Map } from "lucide-react";
import type { Property } from "@shared/schema";
import { usePrefetchPropertyDetails } from "@/hooks/useImagePreloader";
import { apiGet } from "@/lib/api";
import { useIsMobile } from "@/hooks/use-mobile";
import PropertyModalMobile from "@/components/PropertyModalMobile";

export default function PropertySearch() {
  const [location] = useLocation();
  const isMobile = useIsMobile();
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [selectedPropertyForModal, setSelectedPropertyForModal] = useState<Property | null>(null);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [leadFormProperty, setLeadFormProperty] = useState<Property | null>(null);
  const [leadType, setLeadType] = useState<"tour" | "contact">("tour");
  // Using demo property data - integrates with your IDX feed for live listings
  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  const [showAllProperties, setShowAllProperties] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [filters, setFilters] = useState({
    city: "",
    minPrice: "",
    maxPrice: "",
    bedrooms: "",
    bathrooms: "",
    propertyType: "",
    features: [] as string[],
  });

  // Initialize filters from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.split('?')[1] || '');
    setFilters({
      city: searchParams.get('city') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      bedrooms: searchParams.get('bedrooms') || '',
      bathrooms: searchParams.get('bathrooms') || '',
      propertyType: searchParams.get('propertyType') || '',
      features: [],
    });
  }, [location]);

  // Safely convert price to a comparable number (handles "$1,234,567" and "1234567.00")
  const toNumericPrice = (price: number | string | null | undefined): number => {
    if (price === null || price === undefined) return 0;
    if (typeof price === "number") return price;
    const cleaned = price.replace(/[^0-9.]/g, "");
    const value = parseFloat(cleaned);
    return Number.isFinite(value) ? value : 0;
  };

  const { data: properties, isLoading, refetch } = useQuery<Property[]>({
    queryKey: ["/api/properties", { filters }],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      // Add all filter parameters to the request
      params.set("isWaterfront", "true"); // Only waterfront properties
      params.set("fields", "card"); // compact fields for cards
      params.set("limit", "24"); // only fetch what's needed for featured grid
      // Fetch the priciest waterfront properties so featured filter has matches
      params.set("sortField", "price");
      params.set("sortDir", "desc");
      // Ensure luxury threshold for featured feed and bust server cache
      if (!filters.minPrice) {
        params.set("minPrice", "1000000");
      }
      if (filters.city && filters.city.trim()) params.set("city", filters.city);
      if (filters.minPrice && filters.minPrice.trim()) params.set("minPrice", filters.minPrice);
      if (filters.maxPrice && filters.maxPrice.trim()) params.set("maxPrice", filters.maxPrice);
      if (filters.bedrooms && filters.bedrooms.trim()) params.set("bedrooms", filters.bedrooms);
      if (filters.bathrooms && filters.bathrooms.trim()) params.set("bathrooms", filters.bathrooms);
      if (filters.propertyType && filters.propertyType.trim()) params.set("propertyType", filters.propertyType);
      
      const hasFilters = params.toString() !== '';
      console.log(`🔄 Loading properties from server${hasFilters ? ' with filters' : ''}...`);
      
      if (hasFilters) {
        console.log(`🎯 Applied filters: ${params.toString()}`);
      }
      
      try {
        const data = await apiGet<Property[]>(`/api/properties?${params.toString()}`);
        console.log(`📊 Loaded ${data.length} South Florida waterfront properties${hasFilters ? ' (filtered)' : ''}`);
        return data;
      } catch (err) {
        console.error('Failed to fetch properties', err);
        throw err;
      }
    },
  });

  const handleScheduleTour = (propertyId: string) => {
    setSelectedProperty(propertyId);
    setLeadType("tour");
    const found = (properties || []).find(p => p.id === propertyId);
    setLeadFormProperty(found || null);
    setIsLeadFormOpen(true);
    if (found) {
      setSelectedPropertyForModal(found);
      setIsPropertyModalOpen(true);
    }
  };

  const handleContactAgent = (propertyId: string) => {
    setSelectedProperty(propertyId);
    setLeadType("contact");
    const found = (properties || []).find(p => p.id === propertyId);
    setLeadFormProperty(found || null);
    setIsLeadFormOpen(true);
    if (found) {
      setSelectedPropertyForModal(found);
      setIsPropertyModalOpen(true);
    }
  };

  // Handlers used inside PropertyDetailModal (accept Property object)
  const handleScheduleTourFromModal = (property: Property) => {
    setLeadType("tour");
    setLeadFormProperty(property);
    setIsLeadFormOpen(true);
    setSelectedProperty(property.id);
    setSelectedPropertyForModal(property);
    setIsPropertyModalOpen(true);
  };

  const handleContactAgentFromModal = (property: Property) => {
    setLeadType("contact");
    setLeadFormProperty(property);
    setIsLeadFormOpen(true);
    setSelectedProperty(property.id);
    setSelectedPropertyForModal(property);
    setIsPropertyModalOpen(true);
  };

  const handlePropertyClick = (property: Property) => {
    setSelectedPropertyForModal(property);
    setIsPropertyModalOpen(true);
  };

  const handlePropertyModalClose = () => {
    setIsPropertyModalOpen(false);
    setSelectedPropertyForModal(null);
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    console.log(`🔧 Filter changed: ${key} = ${value}`);
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
    // React Query will automatically refetch when filters change due to queryKey dependency
  };

  // const selectedPropertyData = properties?.find(p => p.id === selectedProperty);
  
  // Limit properties for featured section - prioritize high-priced waterfront
  const computeFeatured = (): Property[] | undefined => {
    if (!properties) return undefined;
    const candidates = properties.filter(p => (p as any).isWaterfront !== false);

    const over1M = candidates
      .filter(p => toNumericPrice((p as any).price) >= 1_000_000)
      .sort((a, b) => toNumericPrice((b as any).price) - toNumericPrice((a as any).price))
      .slice(0, 24);

    if (over1M.length > 0) return over1M;

    const over800k = candidates
      .filter(p => toNumericPrice((p as any).price) >= 800_000)
      .sort((a, b) => toNumericPrice((b as any).price) - toNumericPrice((a as any).price))
      .slice(0, 24);

    if (over800k.length > 0) return over800k;

    return candidates
      .sort((a, b) => toNumericPrice((b as any).price) - toNumericPrice((a as any).price))
      .slice(0, 12);
  };

  const featuredProperties = showAllProperties 
    ? properties?.filter(property => {
        const price = toNumericPrice(property.price as any);
        return ((property as any).isWaterfront !== false) && price >= 800000; // $800K+ waterfront only
      })
    : computeFeatured();
  
  // Pagination for filter section
  const totalPages = Math.ceil((properties?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProperties = properties?.slice(startIndex, endIndex);

  // SPEED OPTIMIZATION: Prefetch details for visible properties
  const visiblePropertyIds = (featuredProperties || []).map(p => p.id).filter(Boolean) as string[];
  usePrefetchPropertyDetails(visiblePropertyIds, (featuredProperties?.length || 0) > 0);

  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-deep-navy mb-6">
            Featured <span className="text-luxury-gold">Waterfront</span> Properties
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover exceptional waterfront homes in South Florida's most exclusive neighborhoods
          </p>
        </div>

        {/* Featured Properties Grid */}
        <div className="mb-16">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-96"></div>
              ))}
            </div>
          ) : featuredProperties && featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onScheduleTour={() => {
                    setSelectedProperty(property.id);
                    setLeadType("tour");
                    setLeadFormProperty(property);
                    setIsLeadFormOpen(true);
                    setSelectedPropertyForModal(property);
                    setIsPropertyModalOpen(true);
                  }}
                  onContactAgent={() => {
                    setSelectedProperty(property.id);
                    setLeadType("contact");
                    setLeadFormProperty(property);
                    setIsLeadFormOpen(true);
                    setSelectedPropertyForModal(property);
                    setIsPropertyModalOpen(true);
                  }}
                  onClick={handlePropertyClick}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">
                {properties === undefined ? "Loading properties..." : "No properties found"}
              </div>
              <div className="text-sm text-gray-400 mt-2">
                Total properties loaded: {properties?.length || 0}
              </div>
            </div>
          )}
          
          {!showAllProperties && properties && properties.length >= 24 && (
            <div className="text-center mt-12">
              <div className="mb-4">
                <p className="text-gray-600">
                  Showing 24 of {properties.length} properties
                </p>
              </div>
              <Button 
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.location.href = '/buy';
                  }
                }}
                className="bg-luxury-gold text-deep-navy hover:bg-yellow-500 transition-colors duration-200 px-8 py-3 text-lg font-medium"
              >
                View All Properties
              </Button>
            </div>
          )}
          
          {false && showAllProperties && (
            <div className="text-center mt-12">
              <Button 
                onClick={() => setShowAllProperties(false)}
                variant="outline"
                className="border-luxury-gold text-luxury-gold hover:bg-luxury-gold hover:text-deep-navy transition-colors duration-200 px-8 py-3 text-lg font-medium"
              >
                Show Less
              </Button>
            </div>
          )}
        </div>

        {/* Map Section */}
        <div className="bg-gray-50 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="font-playfair text-4xl md:text-5xl font-bold text-deep-navy mb-6">
              Explore <span className="text-luxury-gold">South FL</span> Waterfront
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              Interactive map showing luxury waterfront properties across Miami, Fort Lauderdale, and the Keys
            </p>
            
            {/* Live Data Badge */}
            <div className="flex justify-center items-center mb-6">
              <Badge className="bg-luxury-gold text-deep-navy px-4 py-2">
                Live Property Data
              </Badge>
            </div>

            {/* View Mode Toggle */}
            <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as "grid" | "map")} className="w-fit mx-auto">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="grid" className="flex items-center gap-2">
                  <Grid className="w-4 h-4" />
                  Grid View
                </TabsTrigger>
                <TabsTrigger value="map" className="flex items-center gap-2">
                  <Map className="w-4 h-4" />
                  Map View
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as "grid" | "map")}>
            <TabsContent value="grid">
              <div className="grid grid-cols-1 lg:grid-cols-4 xl:grid-cols-6 gap-8">
                {/* Filters Sidebar */}
                <div className="lg:col-span-1 xl:col-span-2">
                  <Card className="sticky top-24">
                    <CardHeader>
                      <CardTitle className="font-playfair text-2xl text-deep-navy">Filter Properties</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <Label className="text-deep-navy text-sm font-medium mb-2">Price Range</Label>
                        <div className="flex space-x-2">
                          <Input
                            placeholder="Min"
                            value={filters.minPrice}
                            onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                            className="flex-1"
                          />
                          <Input
                            placeholder="Max"
                            value={filters.maxPrice}
                            onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-deep-navy text-sm font-medium mb-2">Bedrooms</Label>
                        <div className="flex space-x-2">
                          {["Any", "3+", "4+", "5+"].map((option) => (
                            <Button
                              key={option}
                              variant={filters.bedrooms === (option === "Any" ? "" : option.replace("+", "")) ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleFilterChange("bedrooms", option === "Any" ? "" : option.replace("+", ""))}
                              className="flex-1"
                            >
                              {option}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-deep-navy text-sm font-medium mb-2">Bathrooms</Label>
                        <div className="flex space-x-2">
                          {["Any", "2+", "3+", "4+"].map((option) => (
                            <Button
                              key={option}
                              variant={filters.bathrooms === (option === "Any" ? "" : option.replace("+", "")) ? "default" : "outline"}
                              size="sm"
                              onClick={() => handleFilterChange("bathrooms", option === "Any" ? "" : option.replace("+", ""))}
                              className="flex-1"
                            >
                              {option}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-deep-navy text-sm font-medium mb-2">Property Type</Label>
                        <div className="space-y-2">
                          {["Single Family", "Condo", "Estate"].map((type) => (
                            <div key={type} className="flex items-center space-x-2">
                              <Checkbox
                                id={type}
                                checked={filters.propertyType === type}
                                onCheckedChange={(checked) => 
                                  handleFilterChange("propertyType", checked ? type : "")
                                }
                              />
                              <Label htmlFor={type} className="text-sm">{type}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-deep-navy text-sm font-medium mb-2">Amenities</Label>
                        <div className="space-y-2">
                          {["Private Beach", "Boat Dock", "Pool", "Tennis Court"].map((amenity) => (
                            <div key={amenity} className="flex items-center space-x-2">
                              <Checkbox
                                id={amenity}
                                checked={filters.features.includes(amenity)}
                                onCheckedChange={(checked) => {
                                  const newFeatures = checked
                                    ? [...filters.features, amenity]
                                    : filters.features.filter(f => f !== amenity);
                                  handleFilterChange("features", newFeatures);
                                }}
                              />
                              <Label htmlFor={amenity} className="text-sm">{amenity}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full bg-ocean-blue hover:bg-bright-blue text-white"
                        onClick={() => refetch()}
                      >
                        Apply Filters
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Property Grid */}
                <div className="lg:col-span-3 xl:col-span-4">
                  {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="bg-gray-200 aspect-[4/3] rounded-lg mb-4"></div>
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {paginatedProperties?.map((property) => (
                          <PropertyCard
                            key={property.id}
                            property={property}
                            onScheduleTour={() => {
                              setSelectedProperty(property.id);
                              setLeadType("tour");
                              setLeadFormProperty(property);
                              setIsLeadFormOpen(true);
                              setSelectedPropertyForModal(property);
                              setIsPropertyModalOpen(true);
                            }}
                            onContactAgent={() => {
                              setSelectedProperty(property.id);
                              setLeadType("contact");
                              setLeadFormProperty(property);
                              setIsLeadFormOpen(true);
                              setSelectedPropertyForModal(property);
                              setIsPropertyModalOpen(true);
                            }}
                            onClick={handlePropertyClick}
                          />
                        ))}
                      </div>
                      
                      {/* Pagination Controls */}
                      {properties && properties.length > itemsPerPage && (
                        <div className="mt-8 flex flex-col items-center space-y-4">
                          <div className="text-gray-600 text-sm">
                            Showing {startIndex + 1} to {Math.min(endIndex, properties.length)} of {properties.length} properties
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-2"
                            >
                              Previous
                            </Button>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => 
                                page === 1 || 
                                page === totalPages || 
                                Math.abs(page - currentPage) <= 2
                              )
                              .map((page, index, array) => (
                                <div key={page} className="flex items-center">
                                  {index > 0 && array[index - 1] !== page - 1 && (
                                    <span className="px-2 text-gray-400">...</span>
                                  )}
                                  <Button
                                    variant={currentPage === page ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-2 ${
                                      currentPage === page 
                                        ? "bg-luxury-gold text-deep-navy hover:bg-yellow-500" 
                                        : ""
                                    }`}
                                  >
                                    {page}
                                  </Button>
                                </div>
                              ))}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                              disabled={currentPage === totalPages}
                              className="px-3 py-2"
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="map">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Compact Filters Sidebar for Map View */}
                <div className="lg:col-span-1">
                  <Card className="sticky top-24">
                    <CardHeader>
                      <CardTitle className="font-playfair text-xl text-deep-navy">Quick Filters</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-deep-navy text-sm font-medium mb-2">City / Location</Label>
                        <Input
                          placeholder="Miami Beach, Coral Gables, etc."
                          value={filters.city}
                          onChange={(e) => handleFilterChange("city", e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <Label className="text-deep-navy text-sm font-medium mb-2">Price Range</Label>
                        <div className="flex space-x-2">
                          <Input
                            placeholder="Min"
                            value={filters.minPrice}
                            onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                            className="flex-1"
                          />
                          <Input
                            placeholder="Max"
                            value={filters.maxPrice}
                            onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full bg-ocean-blue hover:bg-bright-blue text-white"
                        onClick={() => refetch()}
                      >
                        Update Map
                      </Button>
                      

                    </CardContent>
                  </Card>
                </div>
                
                {/* Map Area */}
                <div className="lg:col-span-3">
                  <PropertyMap 
                    properties={properties || []} 
                    height="600px"
                    className="shadow-xl"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Lead form is rendered inside PropertyDetailModal for correct focus/inert behavior */}

      {isMobile && selectedPropertyForModal && (
        <PropertyModalMobile open={isPropertyModalOpen} onClose={handlePropertyModalClose}>
          <PropertyDetailModal
            property={selectedPropertyForModal}
            isOpen={true}
            onClose={handlePropertyModalClose}
            onScheduleTour={handleScheduleTourFromModal}
            onContactAgent={handleContactAgentFromModal}
            isLeadFormOpen={isLeadFormOpen}
            leadType={leadType}
            onCloseLeadForm={() => setIsLeadFormOpen(false)}
            renderMode="inline"
          />
        </PropertyModalMobile>
      )}

      {!isMobile && selectedPropertyForModal && (
        <PropertyDetailModal
          property={selectedPropertyForModal}
          isOpen={isPropertyModalOpen}
          onClose={handlePropertyModalClose}
          onScheduleTour={handleScheduleTourFromModal}
          onContactAgent={handleContactAgentFromModal}
          isLeadFormOpen={isLeadFormOpen}
          leadType={leadType}
          onCloseLeadForm={() => setIsLeadFormOpen(false)}
        />
      )}
    </div>
  );
}
